import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { API } from "../App";
import axios from "axios";
import { 
  Package, 
  ArrowLeft, 
  Layers, 
  ShoppingBag,
  Star,
  Grid3X3,
  ArrowRight,
  Sparkles,
  Brain,
  ChevronRight,
  Phone,
  MessageCircle
} from "lucide-react";

// Official Achraj Logo URL
const LOGO_URL = "/achraj_logo.png";

// Subtle background
const GridBackground = () => null;

export default function ProductsPage() {
  const [products, setProducts] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  
  // Get category from URL parameter
  const selectedCategory = searchParams.get('category');

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [productsRes, categoriesRes] = await Promise.all([
          axios.get(`${API}/products${selectedCategory ? `?category_id=${selectedCategory}` : ""}`),
          axios.get(`${API}/categories`)
        ]);
        
        if (selectedCategory) {
          setProducts(productsRes.data);
        } else {
          setAllProducts(productsRes.data);
        }
        setCategories(categoriesRes.data);
      } catch (err) {
        console.error("Error:", err);
      }
      setLoading(false);
    };
    fetchData();
  }, [selectedCategory]);

  // Fetch all products for category previews
  useEffect(() => {
    if (!selectedCategory) {
      const fetchAllProducts = async () => {
        try {
          const res = await axios.get(`${API}/products`);
          setAllProducts(res.data);
        } catch (err) {
          console.error("Error:", err);
        }
      };
      fetchAllProducts();
    }
  }, [selectedCategory]);

  const handleCategoryChange = (categoryId) => {
    // Scroll to top when changing category
    window.scrollTo(0, 0);
    if (categoryId) {
      setSearchParams({ category: categoryId });
    } else {
      setSearchParams({});
    }
  };

  const getCategoryName = () => {
    if (!selectedCategory) return null;
    const cat = categories.find(c => c.id === selectedCategory);
    return cat?.name || null;
  };

  // Get products for a specific category (for preview)
  const getProductsForCategory = (categoryId) => {
    return allProducts.filter(p => p.category_id === categoryId).slice(0, 4);
  };

  // Get product count for a category
  const getProductCount = (categoryId) => {
    return allProducts.filter(p => p.category_id === categoryId).length;
  };

  return (
    <div className="min-h-screen bg-white text-gray-900" data-testid="products-page">
      
      {/* Navigation - Light */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-xl border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 sm:h-20">
            <Link to={selectedCategory ? "/products" : "/"} className="flex items-center gap-2 text-gray-500 hover:text-orange-600 transition-colors">
              <ArrowLeft className="w-5 h-5" />
              <span className="hidden sm:inline">{selectedCategory ? "All Categories" : "Back to Home"}</span>
            </Link>
            
            <Link to="/" className="flex items-center gap-2 sm:gap-3">
              <img src={LOGO_URL} alt="Achraj Logo" className="h-10 w-10 sm:h-12 sm:w-12 rounded-full object-cover border-2 border-orange-200" />
              <div className="hidden sm:block">
                <span className="font-bold text-lg text-gray-900">Achraj Services</span>
                <p className="text-xs text-gray-500">Products</p>
              </div>
            </Link>
            
            <Link to="/shop">
              <Button className="bg-orange-500 hover:bg-orange-600 text-white rounded-full" data-testid="products-login-btn">
                <ShoppingBag className="w-4 h-4 mr-2" /> Shop Now
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-4 pt-28 pb-16 relative z-10">
        
        {/* Show Categories View when no category selected */}
        {!selectedCategory ? (
          <>
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-orange-100 border border-orange-200 text-orange-700 rounded-full text-sm font-medium mb-4">
                <Grid3X3 className="w-4 h-4" /> Category Se Shop Karo
              </div>
              <h1 className="text-4xl md:text-5xl font-bold mb-4 text-gray-900">
                Hamari <span className="text-orange-500">Categories</span>
              </h1>
              <p className="text-gray-500 text-lg">Premium products organized by category</p>
            </div>

            {loading ? (
              <div className="text-center py-20">
                <div className="w-16 h-16 border-4 border-orange-200 border-t-orange-500 rounded-full animate-spin mx-auto"></div>
                <p className="text-gray-400 mt-4">Loading categories...</p>
              </div>
            ) : categories.length > 0 ? (
              <div className="space-y-8">
                {categories.map((category) => {
                  const categoryProducts = getProductsForCategory(category.id);
                  const productCount = getProductCount(category.id);
                  
                  return (
                    <div key={category.id} className="group">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <div className="p-3 bg-orange-100 rounded-xl">
                            <Layers className="w-6 h-6 text-orange-600" />
                          </div>
                          <div>
                            <h2 className="text-2xl font-bold text-gray-900">{category.name}</h2>
                            <p className="text-gray-500 text-sm">{productCount} products available</p>
                          </div>
                        </div>
                        <Button onClick={() => handleCategoryChange(category.id)} className="bg-orange-500 hover:bg-orange-600 text-white rounded-full">
                          View All <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                      </div>

                      {categoryProducts.length > 0 ? (
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          {categoryProducts.map((product) => (
                            <div key={product.id} className="cursor-pointer" onClick={() => handleCategoryChange(category.id)}>
                              <Card className="bg-white border border-gray-200 rounded-2xl overflow-hidden hover:shadow-lg hover:border-orange-200 transition-all h-full">
                                <div className="aspect-square bg-gray-50 relative overflow-hidden">
                                  {product.image_url ? (
                                    <img src={product.image_url} alt={product.name} className="w-full h-full object-cover hover:scale-110 transition-transform duration-500" />
                                  ) : (
                                    <div className="w-full h-full flex items-center justify-center"><Package className="w-12 h-12 text-gray-300" /></div>
                                  )}
                                </div>
                                <CardContent className="p-3">
                                  <h3 className="text-gray-900 font-medium text-sm line-clamp-1">{product.name}</h3>
                                  <div className="flex items-center justify-between gap-1 mt-0.5">
                                    <p className="text-lg font-bold text-orange-600">₹{product.price}</p>
                                    <span className="text-[10px] font-bold text-purple-700 bg-purple-100 px-1.5 py-0.5 rounded-full whitespace-nowrap" data-testid="product-pv-badge">{product.point_value || 0} PV</span>
                                  </div>
                                </CardContent>
                              </Card>
                            </div>
                          ))}
                          {productCount > 4 && (
                            <div className="cursor-pointer" onClick={() => handleCategoryChange(category.id)}>
                              <Card className="bg-orange-50 border border-orange-200 rounded-2xl overflow-hidden hover:shadow-lg transition-all h-full">
                                <div className="aspect-square flex flex-col items-center justify-center">
                                  <div className="text-4xl font-bold text-orange-600">+{productCount - 4}</div>
                                  <p className="text-gray-500 text-sm mt-2">More Products</p>
                                  <ChevronRight className="w-6 h-6 text-orange-500 mt-2" />
                                </div>
                              </Card>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="bg-gray-50 border border-gray-200 rounded-2xl p-8 text-center">
                          <Package className="w-12 h-12 text-gray-300 mx-auto mb-3" /><p className="text-gray-400">No products yet</p>
                        </div>
                      )}
                      <div className="mt-8 border-b border-gray-200"></div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-20">
                <Grid3X3 className="w-20 h-20 text-gray-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-700 mb-2">No categories found</h3>
                <p className="text-gray-400">Check back soon!</p>
              </div>
            )}
          </>
        ) : (
          <>
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-orange-100 border border-orange-200 text-orange-700 rounded-full text-sm font-medium mb-4">
                <Layers className="w-4 h-4" /> {getCategoryName()}
              </div>
              <h1 className="text-4xl md:text-5xl font-bold mb-4 text-gray-900">{getCategoryName()}</h1>
              <p className="text-gray-500 text-lg">Browse all products in this category</p>
            </div>

            <div className="flex flex-wrap gap-3 justify-center mb-10">
              <Button variant="outline" onClick={() => handleCategoryChange(null)} className="rounded-full border-gray-300 text-gray-600 hover:border-orange-300 hover:text-orange-600">
                <ArrowLeft className="w-4 h-4 mr-2" /> All Categories
              </Button>
              {categories.map((cat) => (
                <Button key={cat.id} onClick={() => handleCategoryChange(cat.id)}
                  className={`rounded-full transition-all ${selectedCategory === cat.id ? "bg-orange-500 text-white border-0" : "bg-white border border-gray-300 text-gray-600 hover:border-orange-300"}`}>
                  {cat.name}
                </Button>
              ))}
            </div>

            {loading ? (
              <div className="text-center py-20"><div className="w-16 h-16 border-4 border-orange-200 border-t-orange-500 rounded-full animate-spin mx-auto"></div></div>
            ) : products.length > 0 ? (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {products.map((product) => (
                  <Card key={product.id} className="bg-white border border-gray-200 rounded-2xl overflow-hidden hover:shadow-xl hover:border-orange-200 transition-all h-full group">
                    <div className="aspect-square bg-gray-50 relative overflow-hidden">
                      {product.image_url ? (
                        <img src={product.image_url} alt={product.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center"><Package className="w-16 h-16 text-gray-300" /></div>
                      )}
                      {product.stock === 0 && (
                        <div className="absolute inset-0 bg-white/80 flex items-center justify-center"><span className="text-red-500 font-semibold border border-red-300 px-4 py-2 rounded-full bg-red-50">Out of Stock</span></div>
                      )}
                      {product.stock > 0 && (
                        <div className="absolute top-3 right-3"><Badge className="bg-green-100 text-green-700 border border-green-200"><Star className="w-3 h-3 mr-1" /> In Stock</Badge></div>
                      )}
                    </div>
                    <CardContent className="p-5">
                      <h3 className="text-gray-900 font-semibold text-lg mb-2">{product.name}</h3>
                      <p className="text-gray-500 text-sm mb-4 line-clamp-2">{product.description}</p>
                      <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                        <div>
                          <p className="text-xs text-gray-400">Price</p>
                          <p className="text-2xl font-bold text-orange-600">₹{product.price}</p>
                          <span className="inline-block mt-1 text-xs font-bold text-purple-700 bg-purple-100 px-2 py-0.5 rounded-full" data-testid="product-pv-badge">{product.point_value || 0} PV</span>
                        </div>
                        <Link to="/shop"><Button size="sm" className="bg-orange-500 hover:bg-orange-600 text-white rounded-full"><ShoppingBag className="w-4 h-4 mr-1" /> Buy</Button></Link>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <Package className="w-20 h-20 text-gray-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-700 mb-2">No products found</h3>
                <Button onClick={() => handleCategoryChange(null)} className="bg-orange-500 text-white rounded-full mt-4"><ArrowLeft className="w-4 h-4 mr-2" /> Browse All</Button>
              </div>
            )}
          </>
        )}
      </main>

      {/* Footer */}
      <footer className="bg-gray-50 border-t border-gray-200 py-8 px-4 mt-12">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-gray-600 mb-2">Achraj Services Private Limited</p>
          <p className="text-gray-400 text-sm mb-4">Quality products for your daily needs</p>
          <div className="flex items-center justify-center gap-6 text-sm">
            <a href="tel:+919956864243" className="flex items-center gap-2 text-gray-500 hover:text-orange-600"><Phone className="w-4 h-4" /> +91 99568 64243</a>
            <a href="https://wa.me/919956864243" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-gray-500 hover:text-green-600"><MessageCircle className="w-4 h-4" /> WhatsApp</a>
          </div>
        </div>
      </footer>

      <a href="https://wa.me/919956864243" target="_blank" rel="noopener noreferrer" className="fixed bottom-6 right-6 z-50 bg-[#25D366] hover:bg-[#128C7E] text-white p-4 rounded-full shadow-lg transition-all hover:scale-110" data-testid="whatsapp-float-btn">
        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
      </a>
    </div>
  );
}
